import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { companyAdminAPI } from '../services/adminAPI';

const RequestMeetingFromBuyer = () => {
  const { buyerId } = useParams();
  const navigate = useNavigate();
  const [companySlots, setCompanySlots] = useState([]);
  const [buyerSlots, setBuyerSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchSlots = async () => {
    try {
      setSlotsLoading(true);
      const [companyResponse, buyerResponse] = await Promise.all([
        companyAdminAPI.getCompanySlots(),
        companyAdminAPI.getBuyerSlots(parseInt(buyerId))
      ]);
      setCompanySlots(companyResponse.data.slots);
      setBuyerSlots(buyerResponse.data.slots);
    } catch (error) {
      setError('Slots Loading Error');
      console.error('Error fetching slots:', error);
    } finally {
      setSlotsLoading(false);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSlots();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buyerId]);

  // Helper function to check if a slot is available (both company and buyer slots must be available)
  const isSlotAvailable = (companySlot) => {
    if (!companySlot.is_available || companySlot.is_break_time) {
      return false;
    }

    // Find matching buyer slot
    const matchingBuyerSlot = buyerSlots.find(bs => 
      bs.meeting_date === companySlot.meeting_date &&
      bs.start_time === companySlot.start_time &&
      bs.end_time === companySlot.end_time
    );

    if (!matchingBuyerSlot) {
      return false;
    }

    // Both must be available
    return matchingBuyerSlot.is_available && !matchingBuyerSlot.is_break_time;
  };

  const handleRequestMeeting = async () => {
    if (!selectedSlot) {
      setError('Please select a time slot');
      return;
    }
    try {
      await companyAdminAPI.requestMeetingFromBuyer(parseInt(buyerId), selectedSlot.id);
      setError('');
      alert('Meeting request sent successfully!');
      navigate('/admin/company-admin');
    } catch (error) {
      setError(error.response?.data?.message || 'Error sending meeting request');
    }
  };

  const formatTime = (time) => {
    return time ? time.substring(0, 5) : '';
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('tr-TR');
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        Yükleniyor...
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto', backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
        <h1>Request Meeting from Buyer</h1>
        <button
          onClick={() => navigate('/admin/company-admin')}
          style={{
            padding: '8px 16px',
            backgroundColor: '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Back
        </button>
      </div>

      {error && (
        <div style={{
          backgroundColor: '#f8d7da',
          color: '#721c24',
          padding: '12px',
          borderRadius: '4px',
          marginBottom: '20px'
        }}>
          {error}
          <button
            onClick={() => setError('')}
            style={{
              float: 'right',
              background: 'none',
              border: 'none',
              color: '#721c24',
              cursor: 'pointer',
              fontSize: '18px',
              fontWeight: 'bold'
            }}
          >
            ×
          </button>
        </div>
      )}

      <div style={{
        backgroundColor: '#fff',
        padding: '20px',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <h2 style={{ marginBottom: '20px' }}>Select Time Slot</h2>
        
        {slotsLoading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
            Loading slots...
          </div>
        ) : companySlots.filter(slot => isSlotAvailable(slot)).length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
            No available slots (both company and buyer must have matching available slots)
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '15px' }}>
            {companySlots.map((slot) => {
              const available = isSlotAvailable(slot);
              const matchingBuyerSlot = buyerSlots.find(bs => 
                bs.meeting_date === slot.meeting_date &&
                bs.start_time === slot.start_time &&
                bs.end_time === slot.end_time
              );
              
              return (
                <div
                  key={slot.id}
                  onClick={() => available && setSelectedSlot(slot)}
                  style={{
                    padding: '15px',
                    border: selectedSlot?.id === slot.id ? '2px solid #EDB404' : '1px solid #ddd',
                    borderRadius: '8px',
                    cursor: available ? 'pointer' : 'not-allowed',
                    backgroundColor: selectedSlot?.id === slot.id ? '#fff9e6' : (available ? '#f8f9fa' : '#f0f0f0'),
                    opacity: available ? 1 : 0.5,
                    transition: 'all 0.2s ease',
                    position: 'relative'
                  }}
                  title={!available ? (matchingBuyerSlot ? 'Buyer slot is not available' : 'No matching buyer slot') : ''}
                >
                  <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>
                    {formatDate(slot.meeting_date)}
                  </div>
                  <div style={{ color: '#666', fontSize: '0.9rem' }}>
                    {formatTime(slot.start_time)} - {formatTime(slot.end_time)}
                  </div>
                  {!available && (
                    <div style={{ 
                      position: 'absolute', 
                      top: '5px', 
                      right: '5px', 
                      fontSize: '12px', 
                      color: '#dc3545',
                      fontWeight: 'bold'
                    }}>
                      Unavailable
                    </div>
                  )}
                  {available && matchingBuyerSlot && (
                    <div style={{ 
                      marginTop: '8px', 
                      fontSize: '11px', 
                      color: '#28a745',
                      fontStyle: 'italic'
                    }}>
                      ✓ Both available
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
          <button
            onClick={() => navigate('/admin/company-admin')}
            style={{
              padding: '10px 20px',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleRequestMeeting}
            disabled={!selectedSlot}
            style={{
              padding: '10px 20px',
              backgroundColor: selectedSlot ? '#EDB404' : '#ccc',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: selectedSlot ? 'pointer' : 'not-allowed',
              fontWeight: 'bold'
            }}
          >
            Send Request
          </button>
        </div>
      </div>
    </div>
  );
};

export default RequestMeetingFromBuyer;


